import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { config } from '@/lib/config.runtime'
import { useChatTranscript } from '@/hooks/useChatTranscript'
import type { HistoryState, Turn } from '@/hooks/useChatTranscript'
import { useConversations } from '@/hooks/useConversations'
import { useFeedback } from '@/hooks/useFeedback'
import type { FeedbackSubmission } from '@/hooks/useFeedback'
import type { AuthState } from '@/hooks/useAuth'
import type { RoutePath } from '@/hooks/useHashRoute'
import AccountMenu from '@/components/AccountMenu'
import ChatInput from '@/components/ChatInput'
import AnswerView from '@/components/AnswerView'
import ConversationId from '@/components/ConversationId'
import ConversationSidebar from '@/components/ConversationSidebar'
import UserMessage from '@/components/UserMessage'
import Logo from '@/components/Logo'
import styles from './Chat.module.css'

export type ChatProps = {
  auth: AuthState
  /** True only for admins — surfaces the stats + admin-panel entry points in
   * the account menu. Their endpoints are enforced server-side regardless. */
  isAdmin: boolean
  onNavigate: (path: RoutePath) => void
}

const MOBILE_QUERY = '(max-width: 600px)'

export default function Chat({ auth, isAdmin, onNavigate }: ChatProps) {
  const acquireToken = useCallback(async () => {
    if (auth.kind !== 'authed') {
      throw new Error('Not signed in')
    }
    return auth.acquireToken()
  }, [auth])

  const conversations = useConversations({
    baseUrl: config.apiBaseUrl,
    acquireToken,
    enabled: auth.kind === 'authed',
  })
  const refreshConversations = conversations.refresh
  const handleTurnPersisted = useCallback(() => {
    void refreshConversations()
  }, [refreshConversations])

  const { turns, conversationId, historyState, isStreaming, ask, openConversation, reset } =
    useChatTranscript({
      baseUrl: config.apiBaseUrl,
      acquireToken,
      onTurnPersisted: handleTurnPersisted,
    })
  const feedback = useFeedback({ baseUrl: config.apiBaseUrl, acquireToken })

  const [sidebarOpen, setSidebarOpen] = useState(() => !window.matchMedia(MOBILE_QUERY).matches)
  const closeSidebarOnMobile = useCallback(() => {
    if (window.matchMedia(MOBILE_QUERY).matches) setSidebarOpen(false)
  }, [])

  const handleAsk = useCallback(
    (q: string) => {
      feedback.reset()
      void ask(q)
    },
    [ask, feedback],
  )

  const handleSelectConversation = useCallback(
    (id: string) => {
      feedback.reset()
      void openConversation(id)
      closeSidebarOnMobile()
    },
    [feedback, openConversation, closeSidebarOnMobile],
  )

  const handleNewConversation = useCallback(() => {
    feedback.reset()
    reset()
    closeSidebarOnMobile()
  }, [feedback, reset, closeSidebarOnMobile])

  const renameConversation = conversations.rename
  const handleRenameConversation = useCallback(
    (id: string, title: string) => {
      void renameConversation(id, title)
    },
    [renameConversation],
  )

  const removeConversation = conversations.remove
  const handleDeleteConversation = useCallback(
    (id: string) => {
      void removeConversation(id)
      // Deleting the open conversation leaves the transcript orphaned —
      // drop back to a fresh chat.
      if (conversationId === id) {
        feedback.reset()
        reset()
      }
    },
    [removeConversation, conversationId, feedback, reset],
  )

  const feedbackTarget = findFeedbackTarget(turns)

  const handleFeedback = useCallback(
    async (submission: FeedbackSubmission) => {
      if (feedbackTarget === null) return
      await feedback.submit(feedbackTarget.interactionId, submission)
    },
    [feedback, feedbackTarget],
  )

  const transcriptRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)

  const handleScroll = (): void => {
    const el = transcriptRef.current
    if (!el) return
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50
  }

  useEffect(() => {
    if (!stickToBottomRef.current) return
    const el = transcriptRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [turns])

  if (auth.kind === 'loading') {
    return (
      <main className={styles.main}>
        <BrandHeader />
        <p className={styles.muted}>Logger inn…</p>
      </main>
    )
  }
  if (auth.kind === 'unauth') {
    return (
      <main className={styles.main}>
        <BrandHeader />
        <p>Du må logge inn for å bruke OTilie.</p>
        <button className={styles.signin} onClick={() => void auth.login()}>
          Logg inn
        </button>
      </main>
    )
  }

  return (
    <div className={styles.layout}>
      <ConversationSidebar
        state={conversations.state}
        activeConversationId={conversationId}
        open={sidebarOpen}
        onSelect={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onRename={handleRenameConversation}
        onDelete={handleDeleteConversation}
        onClose={() => setSidebarOpen(false)}
      />
      <main className={styles.main}>
        <header className={styles.header}>
          <div className={styles.brand}>
            <button
              className={styles.sidebarToggle}
              aria-label={sidebarOpen ? 'Skjul samtaler' : 'Vis samtaler'}
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen((open) => !open)}
            >
              ☰
            </button>
            <div className={styles.lockup}>
              <Logo variant="text" decorative className={styles.logoText} />
              <Logo variant="icon" className={styles.logoIcon} />
            </div>
            <span className={styles.tagline}>
              <strong>OTilie</strong> · intern kunnskaps­assistent
            </span>
          </div>
          <AccountMenu
            username={auth.account.username}
            onLogout={() => void auth.logout()}
            currentPage="chat"
            onNavigate={onNavigate}
            isAdmin={isAdmin}
          />
        </header>

        <section
          className={styles.transcript}
          ref={transcriptRef}
          onScroll={handleScroll}
          aria-label="Samtaleforløp"
        >
          {renderTranscript(turns, historyState, feedbackTarget, feedback.state, handleFeedback)}
        </section>

        <div className={styles.composer}>
          <p className={styles.disclaimer}>
            ⚠ Dette er AI-genererte svar. Sjekk alltid kildene og verifiser informasjonen selv.
          </p>
          <ChatInput
            onSubmit={handleAsk}
            disabled={isStreaming || historyState.kind === 'loading'}
          />
          <ConversationId id={conversationId} />
        </div>
      </main>
    </div>
  )
}

type FeedbackTarget = { turnId: string; interactionId: string }

function renderTranscript(
  turns: Turn[],
  historyState: HistoryState,
  feedbackTarget: FeedbackTarget | null,
  feedbackState: ReturnType<typeof useFeedback>['state'],
  onFeedback: (submission: FeedbackSubmission) => Promise<void>,
): React.ReactNode {
  if (historyState.kind === 'loading') {
    return <p className={styles.muted}>Laster samtale…</p>
  }
  if (historyState.kind === 'error') {
    return <p className={styles.error}>Kunne ikke laste samtalen: {historyState.message}</p>
  }
  if (turns.length === 0) {
    return <p className={styles.muted}>Still et spørsmål for å starte.</p>
  }
  return turns.map((turn) => (
    <Fragment key={turn.id}>
      {turn.query !== '' && <UserMessage>{turn.query}</UserMessage>}
      {renderTurnAnswer(turn, feedbackTarget, feedbackState, onFeedback)}
    </Fragment>
  ))
}

function renderTurnAnswer(
  turn: Turn,
  feedbackTarget: FeedbackTarget | null,
  feedbackState: ReturnType<typeof useFeedback>['state'],
  onFeedback: (submission: FeedbackSubmission) => Promise<void>,
): React.ReactNode {
  const isFeedbackTarget = feedbackTarget !== null && feedbackTarget.turnId === turn.id
  if (turn.status === 'streaming') {
    return <AnswerView kind="streaming" partialText={turn.partialText} status={turn.statusText} />
  }
  if (turn.status === 'ready') {
    if (isFeedbackTarget) {
      return (
        <AnswerView
          kind="ready"
          result={turn.result}
          feedbackState={feedbackState}
          onFeedback={onFeedback}
        />
      )
    }
    return <AnswerView kind="ready" result={turn.result} />
  }
  if (turn.status === 'history') {
    if (turn.answerText === null) return null
    if (isFeedbackTarget) {
      return (
        <AnswerView
          kind="history"
          text={turn.answerText}
          citations={turn.citations}
          feedbackState={feedbackState}
          onFeedback={onFeedback}
        />
      )
    }
    return <AnswerView kind="history" text={turn.answerText} citations={turn.citations} />
  }
  return (
    <p className={styles.error}>
      Noe gikk galt: {turn.message}
      {turn.httpStatus !== null ? ` (${turn.httpStatus})` : ''}
    </p>
  )
}

/**
 * Feedback attaches to the newest answer that has an interaction id — a live
 * one from this session or a stored assistant message (its interaction_id is
 * the same id the feedback endpoint takes).
 */
function findFeedbackTarget(turns: Turn[]): FeedbackTarget | null {
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i]
    if (t?.status === 'ready') {
      return { turnId: t.id, interactionId: t.result.interactionId }
    }
    if (t?.status === 'history' && t.interactionId !== null && t.answerText !== null) {
      return { turnId: t.id, interactionId: t.interactionId }
    }
  }
  return null
}

function BrandHeader() {
  return (
    <header className={styles.header}>
      <div className={styles.brand}>
        <div className={styles.lockup}>
          <Logo variant="text" decorative className={styles.logoText} />
          <Logo variant="icon" className={styles.logoIcon} />
        </div>
        <span className={styles.tagline}>
          <strong>OTilie</strong> · intern kunnskaps­assistent
        </span>
      </div>
    </header>
  )
}
